<style lang="less" scoped>
  @import "../../../../style/theme.less";

</style>

<template>
  <div>
    <div class="ant-descriptions-title">Edit Block</div>
    <a-empty
      v-if="!reportBlockEditId" :image="emptyImage"
      description="No Block Selected."
    />
    <div v-else>
      <a-descriptions bordered :column="1">
        <a-descriptions-item label="ID">{{ reportBlock.id }}</a-descriptions-item>
        <a-descriptions-item label="Name">

          <a-row :gutter="[16, 16]" type="flex" v-if="!editReportBlockDescriptor">
            <a-col flex="auto">
              <span style="line-height: 32px">
                {{ reportBlock.descriptor }}
              </span>
            </a-col>
            <a-col flex="50px">
              <a-button
                type="primary"
                shape="circle"
                icon="edit"
                @click="editDescriptor"
              />
            </a-col>
          </a-row>

          <a-row :gutter="[16, 16]" type="flex" v-else>
            <a-col flex="auto">
              <a-input
                v-model="updatedDescriptor"
                style="max-width: 100%"
                placeholder="Name"
              />
            </a-col>
            <a-col flex="50px">
              <a-button
                type="primary"
                shape="circle"
                icon="save"
                @click="() => patchReportBlock({ descriptor: updatedDescriptor })"
              />
            </a-col>
          </a-row>

        </a-descriptions-item>
        <a-descriptions-item label="Index">

          <a-row :gutter="[16, 16]" type="flex" v-if="!editReportBlockIndex">
            <a-col flex="auto">
              <span style="line-height: 32px">
                {{ reportBlock.index + 1 }}
              </span>
            </a-col>
            <a-col flex="50px">
              <a-button
                type="primary"
                shape="circle"
                icon="edit"
                @click="editIndex"
              />
            </a-col>
          </a-row>

          <a-row :gutter="[16, 16]" type="flex" v-else>
            <a-col flex="auto">
              <a-input-number
                v-model="updatedIndex"
                :min="1"
                style="max-width: 100%"
                placeholder="Index"
              />
            </a-col>
            <a-col flex="50px">
              <a-button
                type="primary"
                shape="circle"
                icon="save"
                @click="() => patchReportBlock({ index: updatedIndex - 1 })"
              />
            </a-col>
          </a-row>

        </a-descriptions-item>
        <a-descriptions-item label="Conditions" v-if="reportBlock.condition && reportBlock.condition.conditions">
          <conditions-display
            :conditions="reportBlock.condition.conditions"
            :editable="true"
            @edit="openEditConditionsModal"
          />

          <a-modal
            title="Edit Conditions"
            :visible="editConditions"
            width="90%"
            @ok="saveEditConditionsModal"
            @cancel="closeEditConditionsModal"
          >
            <conditions-editor
              ref="conditionsEditor"
              :conditions="reportBlock.condition.conditions"
              @change="conditions => updatedConditions = conditions"
            />
          </a-modal>
        </a-descriptions-item>
        <a-descriptions-item label="Created At">{{ reportBlock.createdAt | timestamp }}</a-descriptions-item>
        <a-descriptions-item label="Updated At">{{ reportBlock.updatedAt | timestamp }}</a-descriptions-item>
      </a-descriptions>

      <div class="spacer-lg" />

      <editor
        v-if="!showEditorPopup"
        ref="editor"
        @save="save"
        @popout="togglePopup"
      />

      <a-modal
        :visible="showEditorPopup"
        :closable="false"
        title="Edit Report Block Content"
        width="80%"
      >
        <template slot="footer">
          <a-button key="back" @click="togglePopup">
            Close
          </a-button>
        </template>

        <editor
          v-if="showEditorPopup"
          ref="editor"
          @save="save"
        />
      </a-modal>
    </div>
  </div>
</template>

<script>
import { Empty } from 'ant-design-vue';
import { mapActions, mapMutations, mapState } from 'vuex';

import conditionsDisplay from '../../../../components/shared/conditions/display';
import conditionsEditor from '../../../../components/shared/conditions/editor';
import editor from '../../../../components/shared/editor/';

export default {
  name: 'admin.report-types.edit-block',

  data: () => ({
    emptyImage: Empty.PRESENTED_IMAGE_SIMPLE,

    showEditorPopup: false,
    updatedDescriptor: undefined,
    updatedIndex: undefined,
    updatedConditions: null,
    editConditions: false,
  }),

  components: {
    editor,
    conditionsDisplay,
    conditionsEditor,
  },

  async mounted() {
    if (!this.reportBlockEditId) {
      return;
    }

    await this.getReportBlock({ id: this.reportBlockEditId, });
    return this.updateEditorContent();
  },

  watch: {
    async reportBlockEditId(id) {
      if (!id) {
        return;
      }

      await this.getReportBlock({ id, });
      return this.updateEditorContent();
    },
  },

  computed: mapState('admin', ['reportBlock', 'reportBlockEditId', 'editReportBlockDescriptor', 'editReportBlockIndex',]),

  methods: {
    ...mapActions('admin', ['getReportBlock', 'updateReportBlockContent', 'patchReportBlock',]),
    ...mapMutations('admin', ['setEditReportBlockDescriptor', 'setEditReportBlockIndex',]),

    editDescriptor() {
      this.updatedDescriptor = this.reportBlock.descriptor;
      this.setEditReportBlockDescriptor(true);
    },
    editIndex() {
      this.updatedIndex = this.reportBlock.index + 1;
      this.setEditReportBlockIndex(true);
    },

    updateEditorContent() {
      const { content, } = this.reportBlock.reportBlockContent || {};
      if (this.$refs.editor) {
        this.$refs.editor.setContent(content);
      }
    },

    async save(content) {
      try {
        await this.updateReportBlockContent(content);
        this.$message.success('Saved successfully', 5);
      } catch (e) {
        this.$message.error('Failed to save', 5);
      } finally {
        this.showEditorPopup = false;
      }
    },

    async togglePopup() {
      this.showEditorPopup = !this.showEditorPopup;
      await this.$nextTick();

      return this.updateEditorContent();
    },

    openEditConditionsModal() {
      this.updatedConditions = null;
      this.editConditions = true;
    },
    closeEditConditionsModal() {
      this.$refs.conditionsEditor.reset();
      this.editConditions = false;
    },
    async saveEditConditionsModal() {
      this.editConditions = false;
      await this.patchReportBlock({ conditions: this.updatedConditions, });
    },
  },
};
</script>
